import { z } from 'zod';
import { papelSchema, senhaSchema } from './auth.ts';
import { dataHoraSchema, paginacaoSchema, respostaListaSchema, uuidSchema } from './comum.ts';

/**
 * Administração de usuários (.spec/013).
 *
 * O acesso efetivo é o cruzamento de duas listas: os **papéis** dizem o que a
 * pessoa pode fazer, as **empresas** dizem sobre quais dados. Um usuário com
 * papel FINANCEIRO e nenhuma empresa vinculada não enxerga nada — e é assim
 * que deve ser, em vez de enxergar tudo por omissão.
 */

const emailSchema = z.string().trim().toLowerCase().email('E-mail inválido.').max(200);

const camposUsuario = {
  nome: z.string().trim().min(2, 'Informe o nome do usuário.').max(200),
  email: emailSchema,
  papeis: z.array(papelSchema).min(1, 'Selecione ao menos um papel.'),
  /**
   * Vínculo com empresas. Vazio só vale para o **administrador** — ver
   * `exigirEmpresaSalvoAdmin`.
   */
  empresas: z.array(uuidSchema).prefault([]),
  ativo: z.boolean().prefault(true),
};

/**
 * Usuário sem empresa não enxerga nada — e isso só é aceitável no ADMIN.
 *
 * O acesso efetivo é o cruzamento das duas listas: os papéis dizem o que a pessoa
 * pode fazer, as empresas dizem sobre quais dados. Um usuário com papel
 * FINANCEIRO e nenhuma empresa entra no sistema e encontra todas as telas vazias
 * — e o suporte que recebe essa ligação não tem como distinguir "sem lotação" de
 * "sem dado cadastrado".
 *
 * **A exceção do ADMIN não é conveniência, é ordem de criação.** Na conta recém
 * provisionada não existe empresa nenhuma para vincular, e é o administrador quem
 * vai cadastrar a primeira. Exigir empresa dele fecharia a porta por dentro.
 *
 * O erro aponta `empresas`, e não a raiz: é o campo que o usuário corrige, e a
 * tela destaca a seção certa.
 */
function exigirEmpresaSalvoAdmin(
  dados: { papeis: readonly string[]; empresas: readonly string[] },
  ctx: z.RefinementCtx,
): void {
  if (dados.empresas.length > 0) return;
  if (dados.papeis.includes('ADMIN')) return;

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['empresas'],
    message:
      'Vincule ao menos uma empresa. Só o administrador pode ficar sem vínculo, porque é ele que cadastra a primeira.',
  });
}

/**
 * Na criação a senha é **opcional**, e ausente é o caminho normal: o usuário
 * recebe um convite por e-mail e define a própria senha. O campo continua
 * existindo como exceção — conta sem e-mail funcionando, por exemplo — e o uso
 * dele fica registrado no log de alterações como senha definida pelo
 * administrador.
 */
export const criarUsuarioSchema = z
  .object({
    ...camposUsuario,
    senha: z.union([senhaSchema, z.literal('')]).optional(),
  })
  .superRefine(exigirEmpresaSalvoAdmin);

export type CriarUsuario = z.infer<typeof criarUsuarioSchema>;

/**
 * Na edição a senha é opcional: em branco significa "manter a atual".
 *
 * Reaproveitar o schema de criação obrigaria o administrador a redigitar a
 * senha para corrigir um nome, e o caminho de menor esforço seria repetir uma
 * senha fraca conhecida.
 */
export const atualizarUsuarioSchema = z
  .object({
    ...camposUsuario,
    senha: z.union([senhaSchema, z.literal('')]).optional(),
  })
  .superRefine(exigirEmpresaSalvoAdmin);

export type AtualizarUsuario = z.infer<typeof atualizarUsuarioSchema>;

export const usuarioSchema = z.object({
  id: uuidSchema,
  nome: z.string(),
  email: z.string(),
  papeis: z.array(papelSchema),
  empresas: z.array(uuidSchema),
  ativo: z.boolean(),
  mfaAtivo: z.boolean(),
  /** Cadastrado sem senha e ainda não aceitou o convite. */
  convitePendente: z.boolean(),
  ultimoLoginEm: dataHoraSchema.nullable(),
  createdAt: dataHoraSchema,
  updatedAt: dataHoraSchema,
});

export type Usuario = z.infer<typeof usuarioSchema>;

/**
 * Resposta do POST: o usuário e se o convite saiu.
 *
 * `null` quando o administrador digitou a senha (não há convite); `false`
 * quando o e-mail falhou — o cadastro **existe**, e a tela oferece o reenvio.
 */
export const usuarioCriadoSchema = usuarioSchema.extend({
  conviteEnviado: z.boolean().nullable(),
});
export type UsuarioCriado = z.infer<typeof usuarioCriadoSchema>;

export const listaUsuariosSchema = respostaListaSchema(usuarioSchema);
export type ListaUsuarios = z.infer<typeof listaUsuariosSchema>;

export const listarUsuariosQuerySchema = paginacaoSchema.extend({
  papel: papelSchema.optional(),
  ativo: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((v) => v === true || v === 'true')
    .optional(),
});

export type ListarUsuariosQuery = z.infer<typeof listarUsuariosQuerySchema>;

export const COLUNAS_ORDENAVEIS_USUARIO = ['nome', 'email', 'ultimoLoginEm', 'createdAt'] as const;

/** Rótulos dos papéis. Um único lugar, para não divergir entre telas. */
export const ROTULO_PAPEL: Readonly<Record<z.infer<typeof papelSchema>, string>> = {
  ADMIN: 'Administrador',
  CONTRATOS: 'Contratos',
  PAGAMENTO: 'Contas a pagar',
  RECEBIMENTO: 'Contas a receber',
  FINANCEIRO: 'Financeiro',
  COMPRAS: 'Compras',
  AUTORIZADOR_COMPRAS: 'Autorizador de compras',
  CONTROLADORIA: 'Controladoria',
  FATURAMENTO: 'Faturamento',
  GERENCIA: 'Gerência',
} as const;

/** Resposta do reenvio de convite e da redefinição forçada. */
export const envioDeLinkSchema = z.object({
  /** Para onde o link foi. */
  email: z.string().email(),
  /** Até quando o link vale. */
  expiraEm: dataHoraSchema,
});
export type EnvioDeLink = z.infer<typeof envioDeLinkSchema>;
